/**
 * ConfettiCanvas
 * Full-screen canvas confetti burst using CubitPlay brand palette.
 * pointer-events: none — never blocks user interaction.
 */
import React, { useEffect, useRef } from 'react';

const PALETTE = [
  '#FF8852', // PS orange
  '#14B8A6', // DC teal
  '#FFD700', // gold
  '#FFFFFF', // white
  '#FF6130', // PS dark
  '#00CDB0', // DC light
  '#FFC453', // amber
];

function rand(lo, hi) { return lo + Math.random() * (hi - lo); }

function spawn(w) {
  return {
    x:     rand(0, w),
    y:     rand(-180, -10),
    vx:    rand(-2.8, 2.8),
    vy:    rand(2, 5.5),
    angle: rand(0, Math.PI * 2),
    spin:  rand(-0.24, 0.24),
    size:  rand(5, 14),
    color: PALETTE[Math.floor(Math.random() * PALETTE.length)],
    kind:  Math.floor(Math.random() * 3), // 0=rect 1=circle 2=triangle
  };
}

function drawParticle(ctx, p, alpha) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle   = p.color;
  ctx.translate(p.x, p.y);
  ctx.rotate(p.angle);
  switch (p.kind) {
    case 0: // confetti strip
      ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      break;
    case 1: // dot
      ctx.beginPath();
      ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
      ctx.fill();
      break;
    default: // triangle
      ctx.beginPath();
      ctx.moveTo(0, -p.size / 2);
      ctx.lineTo( p.size / 2,  p.size / 2);
      ctx.lineTo(-p.size / 2,  p.size / 2);
      ctx.closePath();
      ctx.fill();
  }
  ctx.restore();
}

export function ConfettiCanvas({ duration = 3800, onDone }) {
  const canvasRef = useRef(null);
  const deadRef   = useRef(false);

  useEffect(() => {
    deadRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const N     = 190;
    const parts = Array.from({ length: N }, () => spawn(canvas.width));
    const t0    = performance.now();
    const FADE  = duration - 1400;
    let   raf;

    const tick = (now) => {
      if (deadRef.current) return;
      const elapsed = now - t0;
      const alpha   = elapsed > FADE
        ? Math.max(0, 1 - (elapsed - FADE) / 1400)
        : 1;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < N; i++) {
        const p = parts[i];
        p.x    += p.vx;
        p.y    += p.vy;
        p.vy   += 0.065;        // gravity
        p.angle += p.spin;
        // recycle when off-screen
        if (p.y > canvas.height + 20) Object.assign(p, spawn(canvas.width));
        drawParticle(ctx, p, alpha);
      }

      if (elapsed < duration) {
        raf = requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        onDone?.();
      }
    };

    raf = requestAnimationFrame(tick);

    return () => {
      deadRef.current = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position:      'fixed',
        inset:         0,
        width:         '100vw',
        height:        '100vh',
        pointerEvents: 'none',
        zIndex:        9998,
      }}
    />
  );
}
